import { type Editor } from '@tiptap/react'
import { useCallback, useEffect, useRef, useState } from 'react'

interface TiptapToolbarProps {
    editor: Editor | null
    // Optional preview toggle. When provided, renders an eye icon at the end
    // of the toolbar so the parent can swap the editor for a rendered preview.
    onTogglePreview?: () => void
    isPreviewActive?: boolean
}

const IconBold = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
        <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
    </svg>
)

const IconItalic = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="19" y1="4" x2="10" y2="4" />
        <line x1="14" y1="20" x2="5" y2="20" />
        <line x1="15" y1="4" x2="9" y2="20" />
    </svg>
)

const IconUnderline = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 3v7a6 6 0 0 0 6 6 6 6 0 0 0 6-6V3" />
        <line x1="4" y1="21" x2="20" y2="21" />
    </svg>
)

const IconStrike = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 4H9a3 3 0 0 0-3 3v0a3 3 0 0 0 3 3h6" />
        <path d="M8 20h7a3 3 0 0 0 3-3v0a3 3 0 0 0-3-3H6" />
        <line x1="4" y1="12" x2="20" y2="12" />
    </svg>
)

const IconBulletList = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="9" y1="6" x2="20" y2="6" />
        <line x1="9" y1="12" x2="20" y2="12" />
        <line x1="9" y1="18" x2="20" y2="18" />
        <circle cx="4.5" cy="6" r="1" fill="currentColor" stroke="none" />
        <circle cx="4.5" cy="12" r="1" fill="currentColor" stroke="none" />
        <circle cx="4.5" cy="18" r="1" fill="currentColor" stroke="none" />
    </svg>
)

const IconOrderedList = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="10" y1="6" x2="20" y2="6" />
        <line x1="10" y1="12" x2="20" y2="12" />
        <line x1="10" y1="18" x2="20" y2="18" />
        <text x="3" y="7.5" fontSize="8" fill="currentColor" stroke="none" fontFamily="Arial">
            1
        </text>
        <text x="3" y="13.5" fontSize="8" fill="currentColor" stroke="none" fontFamily="Arial">
            2
        </text>
        <text x="3" y="19.5" fontSize="8" fill="currentColor" stroke="none" fontFamily="Arial">
            3
        </text>
    </svg>
)

const IconLink = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
)

const IconEye = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
        <circle cx="12" cy="12" r="3" />
    </svg>
)

const IconEyeOff = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20C5 20 1 12 1 12a21.77 21.77 0 0 1 5.06-6.06" />
        <path d="M9.9 4.24A10.93 10.93 0 0 1 12 4c7 0 11 8 11 8a21.77 21.77 0 0 1-3.17 4.29" />
        <path d="M1 1l22 22" />
    </svg>
)

const IconChevronDown = () => (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 12 15 18 9" />
    </svg>
)

// Email-safe palette: 11 colors covering common signature/body needs.
// First slot = "Default" (clears the color mark).
const COLOR_SWATCHES = ['#000000', '#374151', '#6B7280', '#DC2626', '#EA580C', '#D97706', '#16A34A', '#0891B2', '#2563EB', '#7C3AED', '#DB2777']

const FONT_SIZE_PRESETS = [8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 28, 36, 48, 72]

export const TiptapToolbar = ({ editor, onTogglePreview, isPreviewActive = false }: TiptapToolbarProps) => {
    const toolbarRef = useRef<HTMLDivElement>(null)
    const [colorOpen, setColorOpen] = useState(false)
    const [sizeOpen, setSizeOpen] = useState(false)
    const [customSize, setCustomSize] = useState('')

    // Run a chain command on the editor while preserving the current selection.
    // Mousedown preventDefault stops the button from stealing DOM focus before
    // the click fires; without this, the editor blurs and the selection is lost,
    // so toggleBold/toggleItalic/etc. have nothing to wrap.
    const runCommand = useCallback(
        (fn: () => void) => (e: React.MouseEvent) => {
            e.preventDefault()
            if (!editor) return
            fn()
        },
        [editor]
    )

    const setLink = useCallback(() => {
        if (!editor) return
        const previousUrl = editor.getAttributes('link').href
        const url = window.prompt('URL', previousUrl)
        if (url === null) return
        if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run()
            return
        }
        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
    }, [editor])

    // Close popovers on outside click. Native color input opens its own picker
    // window — clicks there are outside the toolbar but should not close us.
    useEffect(() => {
        if (!colorOpen && !sizeOpen) return
        const handler = (e: MouseEvent) => {
            if (toolbarRef.current?.contains(e.target as Node)) return
            setColorOpen(false)
            setSizeOpen(false)
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [colorOpen, sizeOpen])

    const applyColor = useCallback(
        (hex: string | null) => {
            if (!editor) return
            if (hex === null) editor.chain().focus().unsetColor().run()
            else editor.chain().focus().setColor(hex).run()
            setColorOpen(false)
        },
        [editor]
    )

    const applyFontSize = useCallback(
        (sizePx: number | null) => {
            if (!editor) return
            if (sizePx === null) editor.chain().focus().unsetFontSize().run()
            else editor.chain().focus().setFontSize(`${sizePx}px`).run()
            setSizeOpen(false)
        },
        [editor]
    )

    const handleCustomSizeSubmit = useCallback(() => {
        const n = parseInt(customSize, 10)
        if (Number.isFinite(n) && n >= 8 && n <= 72) {
            applyFontSize(n)
            setCustomSize('')
        }
    }, [customSize, applyFontSize])

    // When preview is active the editor is hidden, but the toolbar stays mounted
    // so the user can toggle back. In that state we only want the eye button visible.
    if (!editor && !onTogglePreview) return null

    // Formatting buttons are suppressed while previewing — they operate on the
    // hidden editor and would be confusing to click with no visible effect.
    const editorDisabled = !editor || isPreviewActive

    const currentColor = (editor?.getAttributes('textStyle').color as string | undefined) || ''
    const currentFontSize = (editor?.getAttributes('textStyle').fontSize as string | undefined) || ''
    const currentFontSizeLabel = currentFontSize ? currentFontSize.replace('px', '') : 'Size'

    return (
        <div className="tiptap-toolbar" ref={toolbarRef}>
            <button
                type="button"
                onMouseDown={runCommand(() => editor!.chain().focus().toggleBold().run())}
                className={editor?.isActive('bold') ? 'is-active' : ''}
                disabled={editorDisabled}
                title="Bold"
            >
                <IconBold />
            </button>
            <button
                type="button"
                onMouseDown={runCommand(() => editor!.chain().focus().toggleItalic().run())}
                className={editor?.isActive('italic') ? 'is-active' : ''}
                disabled={editorDisabled}
                title="Italic"
            >
                <IconItalic />
            </button>
            <button
                type="button"
                onMouseDown={runCommand(() => editor!.chain().focus().toggleUnderline().run())}
                className={editor?.isActive('underline') ? 'is-active' : ''}
                disabled={editorDisabled}
                title="Underline"
            >
                <IconUnderline />
            </button>
            <button
                type="button"
                onMouseDown={runCommand(() => editor!.chain().focus().toggleStrike().run())}
                className={editor?.isActive('strike') ? 'is-active' : ''}
                disabled={editorDisabled}
                title="Strikethrough"
            >
                <IconStrike />
            </button>

            <div className="toolbar-divider" />

            {/* Color picker */}
            <div className="toolbar-popover-anchor">
                <button
                    type="button"
                    onMouseDown={(e) => {
                        e.preventDefault()
                        if (editorDisabled) return
                        setSizeOpen(false)
                        setColorOpen((v) => !v)
                    }}
                    className={`toolbar-color-btn ${colorOpen ? 'is-active' : ''}`}
                    disabled={editorDisabled}
                    title="Text color"
                    aria-haspopup="true"
                    aria-expanded={colorOpen}
                >
                    <span className="color-swatch" style={{ background: currentColor || '#1f2937' }} />
                    <IconChevronDown />
                </button>
                {colorOpen && (
                    <div className="toolbar-popover" role="dialog">
                        <div className="color-grid">
                            {COLOR_SWATCHES.map((hex) => (
                                <button
                                    key={hex}
                                    type="button"
                                    className={`color-swatch-btn ${currentColor.toLowerCase() === hex.toLowerCase() ? 'is-active' : ''}`}
                                    style={{ background: hex }}
                                    onMouseDown={(e) => {
                                        e.preventDefault()
                                        applyColor(hex)
                                    }}
                                    title={hex}
                                    aria-label={`Set color ${hex}`}
                                />
                            ))}
                        </div>
                        <div className="popover-row">
                            <label className="popover-row-label">
                                Custom
                                <input
                                    type="color"
                                    value={currentColor || '#000000'}
                                    onChange={(e) => applyColor(e.target.value)}
                                    onMouseDown={(e) => e.preventDefault()}
                                    className="color-input"
                                />
                            </label>
                            <button
                                type="button"
                                className="popover-reset-btn"
                                onMouseDown={(e) => {
                                    e.preventDefault()
                                    applyColor(null)
                                }}
                            >
                                Default
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Font size */}
            <div className="toolbar-popover-anchor">
                <button
                    type="button"
                    onMouseDown={(e) => {
                        e.preventDefault()
                        if (editorDisabled) return
                        setColorOpen(false)
                        setSizeOpen((v) => !v)
                    }}
                    className={`toolbar-fontsize-btn ${sizeOpen ? 'is-active' : ''}`}
                    disabled={editorDisabled}
                    title="Font size"
                    aria-haspopup="true"
                    aria-expanded={sizeOpen}
                >
                    <span className="fontsize-label">{currentFontSizeLabel}</span>
                    <IconChevronDown />
                </button>
                {sizeOpen && (
                    <div className="toolbar-popover toolbar-popover-size" role="dialog">
                        <div className="size-custom-row">
                            <input
                                type="number"
                                min={8}
                                max={72}
                                placeholder="Size"
                                value={customSize}
                                onChange={(e) => setCustomSize(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault()
                                        handleCustomSizeSubmit()
                                    }
                                }}
                                className="size-input"
                            />
                            <button type="button" className="size-apply-btn" onMouseDown={(e) => e.preventDefault()} onClick={handleCustomSizeSubmit}>
                                Apply
                            </button>
                        </div>
                        <div className="size-list">
                            <button
                                type="button"
                                className={`size-list-item ${!currentFontSize ? 'is-active' : ''}`}
                                onMouseDown={(e) => {
                                    e.preventDefault()
                                    applyFontSize(null)
                                }}
                            >
                                Default
                            </button>
                            {FONT_SIZE_PRESETS.map((px) => (
                                <button
                                    key={px}
                                    type="button"
                                    className={`size-list-item ${currentFontSize === `${px}px` ? 'is-active' : ''}`}
                                    onMouseDown={(e) => {
                                        e.preventDefault()
                                        applyFontSize(px)
                                    }}
                                >
                                    {px}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <div className="toolbar-divider" />

            <button
                type="button"
                onMouseDown={runCommand(() => editor!.chain().focus().toggleBulletList().run())}
                className={editor?.isActive('bulletList') ? 'is-active' : ''}
                disabled={editorDisabled}
                title="Bullet List"
            >
                <IconBulletList />
            </button>
            <button
                type="button"
                onMouseDown={runCommand(() => editor!.chain().focus().toggleOrderedList().run())}
                className={editor?.isActive('orderedList') ? 'is-active' : ''}
                disabled={editorDisabled}
                title="Numbered List"
            >
                <IconOrderedList />
            </button>

            <div className="toolbar-divider" />

            <button
                type="button"
                onMouseDown={runCommand(setLink)}
                className={editor?.isActive('link') ? 'is-active' : ''}
                disabled={editorDisabled}
                title="Link"
            >
                <IconLink />
            </button>

            {onTogglePreview && (
                <>
                    <div className="toolbar-spacer" />
                    <button
                        type="button"
                        onClick={onTogglePreview}
                        className={`toolbar-preview-btn ${isPreviewActive ? 'is-active' : ''}`}
                        title={isPreviewActive ? 'Back to edit' : 'Preview with sample data'}
                        aria-pressed={isPreviewActive}
                    >
                        {isPreviewActive ? <IconEyeOff /> : <IconEye />}
                    </button>
                </>
            )}
        </div>
    )
}
